/**
 * Color utility functions for generating consistent colors from strings
 * 
 * Uses hash-based color generation in LCH space (Lightness, Chroma, Hue) with support for
 * different color themes (danger, success, neutral, muted)
 * 
 * LCH provides perceptually uniform colors that are easier to fine-tune than HSL
 */

export type ColorTheme = 'danger' | 'success' | 'neutral' | 'muted';

interface LCHRange {
  hueMin: number;
  hueMax: number;
  chromaMin: number;
  chromaMax: number;
  lightnessMin: number;
  lightnessMax: number;
}

/**
 * Theme configurations defining LCH ranges for each color theme
 * 
 * LCH values:
 * - Lightness: 0-100 (0 = black, 100 = white)
 * - Chroma: 0-150+ (0 = gray, higher = more saturated)
 * - Hue: 0-360 (degrees on the color wheel)
 */
const THEME_CONFIGS: Record<ColorTheme, LCHRange> = {
  danger: {
    hueMin: 30,
    hueMax: 45,
    chromaMin: 60,
    chromaMax: 120,
    lightnessMin: 60,
    lightnessMax: 75,
  },
  success: {
    hueMin: 120,
    hueMax: 180,
    chromaMin: 60,
    chromaMax: 120,
    lightnessMin: 45,
    lightnessMax: 60,
  },
  neutral: {
    hueMin: 200,
    hueMax: 320,
    chromaMin: 60,
    chromaMax: 120,
    lightnessMin: 45,
    lightnessMax: 60,
  },
  muted: {
    hueMin: 0,
    hueMax: 360,
    chromaMin: 30,
    chromaMax: 50,
    lightnessMin: 55,
    lightnessMax: 70,
  },
};

/**
 * Simple string hash function that generates consistent numeric values
 * Uses a multiplicative hash approach for good distribution
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Maps a hash value to a range [min, max]
 */
function mapToRange(hash: number, min: number, max: number): number {
  const normalized = (hash % 10000) / 10000; // Normalize to [0, 1]
  return min + normalized * (max - min);
}

/**
 * Generates a consistent LCH color from a string with optional theme
 * 
 * @param str - The string to generate a color from (e.g., job type name)
 * @param theme - Optional color theme ('danger', 'success', 'neutral', 'muted')
 * @returns CSS LCH color string, e.g., "lch(70% 80 230)"
 */
export function generateColorFromString(
  str: string,
  theme: ColorTheme = 'muted'
): string {
  const hash = hashString(str);
  const config = THEME_CONFIGS[theme];

  // Generate three different hash values for hue, chroma, and lightness
  // by using different portions of the hash
  const hueHash = hash;
  const chromaHash = hash >> 8;
  const lightnessHash = hash >> 16;

  const hue = Math.round(mapToRange(hueHash, config.hueMin, config.hueMax));
  const chroma = Math.round(mapToRange(chromaHash, config.chromaMin, config.chromaMax));
  const lightness = Math.round(
    mapToRange(lightnessHash, config.lightnessMin, config.lightnessMax)
  );

  return `lch(${lightness}% ${chroma} ${hue})`;
}

